import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Star, ShieldCheck, Award, MessageCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CLEANING_IMAGES, WHATSAPP_URL } from '../constants/images';
export function Hero() {
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 150]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  return <section id="home" className="relative min-h-screen w-full flex items-center justify-center overflow-hidden" data-id="element-254">
      {/* Full-bleed background image with parallax */}
      <motion.div style={{
      y
    }} className="absolute inset-0 z-0" data-id="element-255">
        <img src={CLEANING_IMAGES[3]} alt="" className="w-full h-[120%] object-cover" data-id="element-256" />
        <div className="absolute inset-0 bg-brand-navy/75" data-id="element-257" />
        <div className="absolute inset-0 bg-slate-950/30" data-id="element-258" />
      </motion.div>

      {/* Floating decorative elements */}
      <motion.div className="absolute top-32 left-10 w-24 h-24 rounded-full bg-brand-green/20 blur-2xl z-10" animate={{
      y: [0, -20, 0],
      x: [0, 15, 0]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-259" />
      <motion.div className="absolute bottom-32 right-10 w-32 h-32 rounded-full bg-brand-gold/20 blur-2xl z-10" animate={{
      y: [0, 20, 0],
      x: [0, -15, 0]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-260" />

      {/* Floating image collage on right (desktop only) */}
      <div className="hidden xl:block absolute right-12 top-1/2 -translate-y-1/2 z-10 w-[420px]" data-id="element-261">
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 1,
        delay: 0.4
      }} className="relative h-[520px]" data-id="element-262">
          <motion.img animate={{
          y: [0, -10, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} src={CLEANING_IMAGES[7]} alt="Clean home interior" className="absolute top-0 right-0 w-72 h-96 object-cover rounded-3xl shadow-2xl border-4 border-white/20" data-id="element-263" />
          <motion.img animate={{
          y: [0, 15, 0]
        }} transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.5
        }} src={CLEANING_IMAGES[12]} alt="Sparkling room" className="absolute bottom-0 left-0 w-64 h-80 object-cover rounded-3xl shadow-2xl border-4 border-white/20" data-id="element-264" />
        </motion.div>
      </div>

      {/* Content */}
      <motion.div style={{
      opacity
    }} className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full" data-id="element-265">
        <div className="max-w-3xl" data-id="element-266">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white text-sm font-semibold mb-8" data-id="element-267">
            <Sparkles className="w-4 h-4 text-brand-gold fill-brand-gold" data-id="element-268" />
            <span data-id="element-269">Serving Clifton, NJ & Surrounding Areas</span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] mb-8 font-serif tracking-tight" data-id="element-270">
            Sparkling Clean.
            <br data-id="element-271" />
            <span className="italic font-light text-white/95" data-id="element-272">Every</span>{' '}
            <span className="relative inline-block" data-id="element-273">
              <span className="relative z-10" data-id="element-274">Single</span>
              <motion.span initial={{
              scaleX: 0
            }} animate={{
              scaleX: 1
            }} transition={{
              duration: 0.8,
              delay: 1
            }} style={{
              originX: 0
            }} className="absolute bottom-2 left-0 right-0 h-3 md:h-4 bg-brand-green/70 -z-0" data-id="element-275" />
            </span>{' '}
            <span className="italic font-light text-white/95" data-id="element-276">Time.</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl leading-relaxed font-light" data-id="element-277">
            Premium residential & commercial cleaning in Clifton, NJ. 15+ years
            of trusted excellence — book in seconds via WhatsApp or text.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-278">
            <Link to="/quote" className="group inline-flex justify-center items-center gap-2 px-8 py-4 rounded-full bg-brand-green text-white font-bold text-lg hover:bg-brand-greenDark transition-all hover:shadow-glow hover:-translate-y-1" data-id="element-279">
              Get a Free Quote
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-280" />
            </Link>
            <Link to="/services" className="inline-flex justify-center items-center px-8 py-4 rounded-full bg-white/10 backdrop-blur-md text-white border-2 border-white/30 font-bold text-lg hover:bg-white hover:text-brand-navy transition-all" data-id="element-281">
              Explore Services
            </Link>
          </motion.div>

          {/* Trust Bar */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="flex flex-wrap items-center gap-x-8 gap-y-4 pt-8 border-t border-white/20" data-id="element-282">
            <div className="flex items-center gap-2 text-white" data-id="element-283">
              <Award className="w-5 h-5 text-brand-gold" data-id="element-284" />
              <span className="text-sm md:text-base font-semibold" data-id="element-285">
                15+ Years Experience
              </span>
            </div>
            <div className="flex items-center gap-2 text-white" data-id="element-286">
              <div className="flex" data-id="element-287">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-brand-gold text-brand-gold" data-id="element-288" />)}
              </div>
              <span className="text-sm md:text-base font-semibold" data-id="element-289">
                5-Star Rated
              </span>
            </div>
            <div className="flex items-center gap-2 text-white" data-id="element-290">
              <ShieldCheck className="w-5 h-5 text-brand-gold" data-id="element-291" />
              <span className="text-sm md:text-base font-semibold" data-id="element-292">
                Fully Insured
              </span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-2 text-white/70" data-id="element-293">
        <span className="text-xs uppercase tracking-widest font-semibold" data-id="element-294">
          Scroll
        </span>
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        duration: 1.5,
        repeat: Infinity
      }} className="w-px h-12 bg-white/40" data-id="element-295" />
      </motion.div>
    </section>;
}